import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { Info } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useCreateUser, useUpdateUser } from '../hooks/useUsers';
import { Roles, ROLE_DISPLAY } from '../userTypes';
import { useAuthStore } from '@/store/authStore';

const userFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type UserFormValues = z.infer<typeof userFormSchema>;

export function AddUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const { roles: userRoles } = useAuthStore();
  const admin = userRoles.includes(Roles.SuperAdmin);
  const isIntegratorAdmin = userRoles.includes(Roles.IntegratorAdmin);

  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [roleError, setRoleError] = useState<string | null>(null);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
  });

  const handleRoleToggle = (role: Roles) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
    setRoleError(null);
  };

  const currentEmail = form.watch('email');
  const hasImplicitWatchfulGoAccess = selectedRoles.has(Roles.SuperAdmin) || selectedRoles.has(Roles.IntegratorAdmin);

  const WATCHFUL_GO_ROLES_RANKED = [Roles.CompanyAdmin, Roles.ProjectAdmin, Roles.User] as const;

  const selectedWatchfulGoRole = WATCHFUL_GO_ROLES_RANKED.find((r) => selectedRoles.has(r)) ?? null;

  const handleWatchfulGoRoleSelect = (role: Roles) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      for (const r of WATCHFUL_GO_ROLES_RANKED) {
        next.delete(r);
      }
      if (role !== selectedWatchfulGoRole) {
        next.add(role);
      }
      return next;
    });
    setRoleError(null);
  };

  const canAssignRole = (role: Roles): boolean => {
    if (admin) return true;
    if (isIntegratorAdmin) return role !== Roles.SuperAdmin;
    return false;
  };

  const onSubmit = async (formData: UserFormValues) => {
    try {
      const rolesArray = Array.from(selectedRoles);

      if (rolesArray.length === 0) {
        setRoleError('Please select at least one role');
        return;
      }

      const data = {
        ...formData,
        roles: rolesArray,
      };

      if (id) {
        await updateUser.mutateAsync({ id, data });
      } else {
        await createUser.mutateAsync(data);
      }
      navigate('/users');
    } catch (error) {
      console.error('Failed to save user:', error);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader data-onboarding="add-user-header">
              <CardTitle className="text-lime">User Information</CardTitle>
              <CardDescription>Add a new user to the system</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div data-onboarding="add-user-basic-info" className="space-y-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Section A: Monitoring Portal */}
          <Card data-onboarding="add-user-role-type">
            <CardHeader>
              <CardTitle className="text-lime">Monitoring Portal</CardTitle>
              <CardDescription>Access to the monitoring portal for alert handling</CardDescription>
            </CardHeader>
            <CardContent>
              <label
                data-onboarding="add-user-role-monitoring"
                className="flex cursor-pointer items-start gap-3"
              >
                <Checkbox
                  checked={selectedRoles.has(Roles.Monitoring)}
                  onCheckedChange={() => handleRoleToggle(Roles.Monitoring)}
                  disabled={!canAssignRole(Roles.Monitoring)}
                />
                <div>
                  <p className="font-medium text-white">{ROLE_DISPLAY[Roles.Monitoring].label}</p>
                  <p className="text-sm text-muted-foreground">{ROLE_DISPLAY[Roles.Monitoring].description}</p>
                </div>
              </label>
            </CardContent>
          </Card>

          {/* Section B: Admin Portal */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lime">Admin Portal</CardTitle>
              <CardDescription>Access to the admin portal for system configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {admin && (currentEmail.endsWith('@watchful.co.nz') || currentEmail.endsWith('@watchfulhq.com')) && (
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={selectedRoles.has(Roles.SuperAdmin)}
                    onCheckedChange={() => handleRoleToggle(Roles.SuperAdmin)}
                  />
                  <div>
                    <p className="font-medium text-white">{ROLE_DISPLAY[Roles.SuperAdmin].label}</p>
                    <p className="text-sm text-muted-foreground">{ROLE_DISPLAY[Roles.SuperAdmin].description}</p>
                  </div>
                </label>
              )}
              <label
                data-onboarding="add-user-role-admin"
                className="flex cursor-pointer items-start gap-3"
              >
                <Checkbox
                  checked={selectedRoles.has(Roles.IntegratorAdmin)}
                  onCheckedChange={() => handleRoleToggle(Roles.IntegratorAdmin)}
                  disabled={!canAssignRole(Roles.IntegratorAdmin)}
                />
                <div>
                  <p className="font-medium text-white">{ROLE_DISPLAY[Roles.IntegratorAdmin].label}</p>
                  <p className="text-sm text-muted-foreground">{ROLE_DISPLAY[Roles.IntegratorAdmin].description}</p>
                </div>
              </label>
              {hasImplicitWatchfulGoAccess && (
                <div className="mt-2 flex items-start gap-2 rounded-md bg-blue-500/10 p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                  <p className="text-sm text-blue-400">
                    {selectedRoles.has(Roles.SuperAdmin) ? ROLE_DISPLAY[Roles.SuperAdmin].label : ROLE_DISPLAY[Roles.IntegratorAdmin].label} role includes full Watchful Go access
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section C: Watchful Go */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lime">Watchful Go</CardTitle>
              <CardDescription>Mobile app access for arm/disarm and site management</CardDescription>
            </CardHeader>
            <CardContent>
              {hasImplicitWatchfulGoAccess ? (
                <div className="flex items-start gap-2 rounded-md bg-blue-500/10 p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                  <p className="text-sm text-blue-400">
                    This user will have access to all sites via their {selectedRoles.has(Roles.SuperAdmin) ? ROLE_DISPLAY[Roles.SuperAdmin].label : ROLE_DISPLAY[Roles.IntegratorAdmin].label} role
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <label
                      data-onboarding="add-user-role-full"
                      className="flex cursor-pointer items-start gap-3"
                    >
                      <Checkbox
                        checked={selectedWatchfulGoRole === Roles.ProjectAdmin}
                        onCheckedChange={() => handleWatchfulGoRoleSelect(Roles.ProjectAdmin)}
                        disabled={!canAssignRole(Roles.ProjectAdmin)}
                      />
                      <div>
                        <p className="font-medium text-white">Site Admin</p>
                        <p className="text-sm text-muted-foreground">{ROLE_DISPLAY[Roles.ProjectAdmin].description}</p>
                      </div>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        checked={selectedWatchfulGoRole === Roles.User}
                        onCheckedChange={() => handleWatchfulGoRoleSelect(Roles.User)}
                        disabled={!canAssignRole(Roles.User)}
                      />
                      <div>
                        <p className="font-medium text-white">Basic User</p>
                        <p className="text-sm text-muted-foreground">{ROLE_DISPLAY[Roles.User].description}</p>
                      </div>
                    </label>
                  </div>

                  <div>
                    <p className="font-medium text-white">Site Access</p>
                    <p className="text-sm text-muted-foreground">
                      Site assignments can be managed after the user has been created.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {roleError && (
            <p className="text-sm text-red-500">{roleError}</p>
          )}

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" data-onboarding="add-user-save-button">Save</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};